import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptInvite,
  cancelInvite,
  fetchInvites,
  rejectInvite,
} from '../lib/api';
import type { ApiError, Invite } from '../lib/api';
import { Skeleton } from './Skeleton';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function InvitesSection() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['invites'],
    queryFn: () => fetchInvites(),
  });

  if (isLoading) {
    return (
      <div className="settings-section">
        <h3>Pending Invites</h3>
        <Skeleton
          className="settings-stub-banner"
          ariaLabel="Loading invites"
          rows={2}
          variant="list"
        />
      </div>
    );
  }

  if (isError) {
    const apiError = error as unknown as ApiError | undefined;
    return (
      <div className="settings-section">
        <h3>Pending Invites</h3>
        <div className="login-form-error" role="alert">
          {apiError?.status === 401
            ? 'Sign in to view your invites.'
            : "We couldn't load your invites. Try refreshing the page."}
        </div>
      </div>
    );
  }

  const invites = data?.invites ?? [];
  const incoming = invites.filter((i) => i.direction === 'incoming');
  const outgoing = invites.filter((i) => i.direction === 'outgoing');

  return (
    <div className="settings-section">
      <h3>Pending Invites</h3>

      <h4>Incoming</h4>
      {incoming.length === 0 ? (
        <p className="settings-stub-banner" role="status">
          No pending invites
        </p>
      ) : (
        <ul className="contacts-list" aria-label="Incoming invites">
          {incoming.map((invite) => (
            <IncomingRow key={invite.username} invite={invite} />
          ))}
        </ul>
      )}

      <h4>Outgoing</h4>
      {outgoing.length === 0 ? (
        <p className="settings-stub-banner" role="status">
          No pending invites
        </p>
      ) : (
        <ul className="contacts-list" aria-label="Outgoing invites">
          {outgoing.map((invite) => (
            <OutgoingRow key={invite.username} invite={invite} />
          ))}
        </ul>
      )}
    </div>
  );
}

function IncomingRow({ invite }: { invite: Invite }) {
  const queryClient = useQueryClient();
  const onSettled = () => queryClient.invalidateQueries({ queryKey: ['invites'] });

  const acceptMut = useMutation({
    mutationFn: () => acceptInvite(invite.username),
    onSettled,
  });
  const rejectMut = useMutation({
    mutationFn: () => rejectInvite(invite.username),
    onSettled,
  });

  const busy = acceptMut.isPending || rejectMut.isPending;
  const mutError = (acceptMut.error ?? rejectMut.error) as ApiError | null;

  return (
    <li className="contacts-row">
      <div className="contacts-row-main">
        <div className="contacts-row-name">
          {invite.display_name ?? invite.username}
        </div>
        <div className="contacts-row-meta">
          <code className="settings-cli-hint-code">{invite.username}</code>
          <span className="contacts-row-meta-sep" aria-hidden="true">·</span>
          <span>Sent {formatDate(invite.sent_at)}</span>
        </div>
        {mutError && (
          <p className="settings-error" role="alert">{mutError.message}</p>
        )}
      </div>
      <div className="settings-actions">
        <button
          type="button"
          className="settings-action-primary"
          disabled={busy}
          onClick={() => acceptMut.mutate()}
        >
          {acceptMut.isPending ? 'Accepting…' : 'Accept'}
        </button>
        <button
          type="button"
          className="settings-action-danger"
          disabled={busy}
          onClick={() => rejectMut.mutate()}
        >
          {rejectMut.isPending ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </li>
  );
}

function OutgoingRow({ invite }: { invite: Invite }) {
  const queryClient = useQueryClient();

  const cancelMut = useMutation({
    mutationFn: () => cancelInvite(invite.username),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invites'] }),
  });

  const mutError = cancelMut.error as ApiError | null;

  return (
    <li className="contacts-row">
      <div className="contacts-row-main">
        <div className="contacts-row-name">
          {invite.display_name ?? invite.username}
        </div>
        <div className="contacts-row-meta">
          <code className="settings-cli-hint-code">{invite.username}</code>
          <span className="contacts-row-meta-sep" aria-hidden="true">·</span>
          <span>Sent {formatDate(invite.sent_at)}</span>
        </div>
        {mutError && (
          <p className="settings-error" role="alert">{mutError.message}</p>
        )}
      </div>
      <div className="settings-actions">
        <button
          type="button"
          className="settings-action-danger"
          disabled={cancelMut.isPending}
          onClick={() => cancelMut.mutate()}
        >
          {cancelMut.isPending ? 'Cancelling…' : 'Cancel'}
        </button>
      </div>
    </li>
  );
}
